/**
 * Vanilla-DOM notify-me modal and inline form. Zero runtime dependencies and
 * SSR-safe: no DOM access happens at module load, only inside openModal /
 * openInlineForm calls (which require a browser).
 *
 * Behavior mirrors the theme app embed's modal
 * (shopify/extensions/src/theme-app-extension/assets/components/Modal.js):
 * same settings-driven texts/colors, same `stoq:restock-modal:*` events, same
 * backend error mapping. Single-form simplifications (no variant dropdown, no
 * channel selector/name/quantity/consent inputs) are documented in
 * docs/modal.md.
 */
import { emit, EVENT_MODAL_CLOSED, EVENT_MODAL_OPENED } from '../events'
import { toNumericId, warn } from '../util'
import { variantIdOf } from '../variant-input'
import { el, focusFirstInput, injectStyles, trapFocus } from './dom'
import {
  buildSignupInput,
  type FormErrorKind,
  mapSubmitResult,
  resolveChannels,
  usedEmailChannel,
  validateForm,
} from './form'
import { resolveStyles, resolveTexts, successMessage } from './texts'
import type {
  InlineFormOptions,
  ModalContext,
  ModalHandle,
  ModalOptions,
  ModalTexts,
} from './types'

export type {
  InlineFormOptions,
  ModalContext,
  ModalHandle,
  ModalOptions,
  ModalPrefill,
  ModalStyles,
  ModalTexts,
  ResolvedModalStyles,
} from './types'
export { DEFAULT_STYLES, DEFAULT_TEXTS, resolveStyles, resolveTexts, successMessage } from './texts'
export {
  buildSignupInput,
  isValidEmail,
  mapSubmitResult,
  normalizePhone,
  resolveChannels,
  usedEmailChannel,
  validateForm,
} from './form'

let uid = 0

/** The currently open modal (at most one at a time). */
let activeModal: ModalHandle | null = null

/** Mounted inline form roots → the container they were mounted against. */
const inlineForms = new Map<HTMLElement, Element>()

function errorTextFor(kind: FormErrorKind, texts: ModalTexts): string {
  switch (kind) {
    case 'email':
      return texts.emailError
    case 'phone':
      return texts.phoneError
    case 'empty':
      return texts.emptyError
    case 'duplicate':
      return texts.duplicateError
    default:
      return texts.genericError
  }
}

/** toNumericId that degrades to null instead of throwing (event payloads). */
function safeNumericId(value: number | string | undefined): number | null {
  if (value === undefined) return null
  try {
    return toNumericId(value)
  } catch {
    return null
  }
}

/** Event payload per api_docs/custom-events.mdx. */
function eventDetail(action: 'opened' | 'closed', options: ModalOptions, variantId: number) {
  return {
    action,
    product: {
      id: safeNumericId(options.productId),
      title: options.productTitle ?? null,
    },
    variant: { id: variantId, title: options.variantTitle ?? null },
  }
}

function requireDocument(method: string): Document {
  if (typeof document === 'undefined') {
    throw new Error(`[Stoq] ${method} requires a browser environment (no document found)`)
  }
  return document
}

/** Builds the signup form root (shared by the modal and the inline form). */
function buildForm(
  ctx: ModalContext,
  options: ModalOptions,
  doc: Document,
  inline: boolean,
  onClose: (() => void) | null,
): HTMLElement {
  const texts = resolveTexts(ctx.settings, options.texts)
  const styles = resolveStyles(ctx.settings, options.styles)
  const channels = resolveChannels(ctx.settings)
  const id = ++uid

  const root = el(doc, 'div', {
    className: inline ? 'stoq-inline-form' : 'stoq-modal',
    attrs: inline
      ? {}
      : {
        role: 'dialog',
        'aria-modal': 'true',
        'aria-labelledby': `stoq-form-header-${id}`,
        'aria-describedby': `stoq-form-description-${id}`,
        tabindex: '-1',
      },
  })
  root.style.color = styles.textColor
  root.style.backgroundColor = styles.backgroundColor
  root.style.borderRadius = styles.borderRadius
  root.style.fontFamily = styles.fontFamily

  if (onClose) {
    const close = el(doc, 'button', {
      className: 'stoq-form-close',
      text: '×',
      attrs: { type: 'button', 'aria-label': texts.closeLabel },
    })
    close.addEventListener('click', onClose)
    root.appendChild(close)
  }

  if (options.imageUrl !== undefined && options.imageUrl !== '') {
    root.appendChild(el(doc, 'img', {
      className: 'stoq-form-image',
      attrs: { src: options.imageUrl, alt: options.productTitle ?? '' },
    }))
  }

  root.appendChild(el(doc, 'h2', {
    className: 'stoq-form-header',
    text: texts.header,
    attrs: { id: `stoq-form-header-${id}` },
  }))
  // description/terms/success are merchant-configured HTML, same as the embed.
  root.appendChild(el(doc, 'div', {
    className: 'stoq-form-description',
    html: texts.description,
    attrs: { id: `stoq-form-description-${id}` },
  }))

  const productLabel = [options.productTitle, options.variantTitle].filter(Boolean).join(' — ')
  if (productLabel !== '') {
    root.appendChild(el(doc, 'p', { className: 'stoq-form-product', text: productLabel }))
  }

  const form = el(doc, 'form', { attrs: { novalidate: '' } })
  root.appendChild(form)

  let emailInput: HTMLInputElement | null = null
  if (channels.email) {
    const field = el(doc, 'div', { className: 'stoq-form-field' })
    field.appendChild(el(doc, 'label', {
      className: 'stoq-sr-only',
      text: texts.emailLabel,
      attrs: { for: `stoq-form-email-${id}` },
    }))
    emailInput = el(doc, 'input', {
      className: 'stoq-form-input',
      attrs: {
        id: `stoq-form-email-${id}`,
        type: 'email',
        autocomplete: 'email',
        placeholder: texts.emailPlaceholder,
      },
    })
    if (options.prefill?.email) emailInput.value = options.prefill.email
    field.appendChild(emailInput)
    form.appendChild(field)
  }

  let phoneInput: HTMLInputElement | null = null
  if (channels.phone) {
    const field = el(doc, 'div', { className: 'stoq-form-field' })
    field.appendChild(el(doc, 'label', {
      className: 'stoq-sr-only',
      text: texts.phoneLabel,
      attrs: { for: `stoq-form-phone-${id}` },
    }))
    phoneInput = el(doc, 'input', {
      className: 'stoq-form-input',
      attrs: {
        id: `stoq-form-phone-${id}`,
        type: 'tel',
        autocomplete: 'tel',
        placeholder: texts.phonePlaceholder,
      },
    })
    if (options.prefill?.phone) phoneInput.value = options.prefill.phone
    field.appendChild(phoneInput)
    form.appendChild(field)
  }

  const errorEl = el(doc, 'p', {
    className: 'stoq-form-error',
    attrs: { 'aria-live': 'polite', id: `stoq-form-error-${id}` },
  })
  errorEl.style.display = 'none'
  form.appendChild(errorEl)

  const button = el(doc, 'button', {
    className: 'stoq-form-button',
    text: texts.buttonText,
    attrs: { type: 'submit' },
  })
  button.style.color = styles.buttonTextColor
  button.style.backgroundColor = styles.buttonBackgroundColor
  form.appendChild(button)

  if (texts.terms !== '') {
    root.appendChild(el(doc, 'div', { className: 'stoq-form-terms', html: texts.terms }))
  }

  const setError = (message: string, duplicate: boolean): void => {
    errorEl.textContent = message
    errorEl.className = `stoq-form-error${duplicate ? ' stoq-form-error--duplicate' : ''}`
    errorEl.style.display = ''
  }

  const clearError = (): void => {
    errorEl.textContent = ''
    errorEl.style.display = 'none'
    emailInput?.classList.remove('stoq-form-input--error')
    phoneInput?.classList.remove('stoq-form-input--error')
  }

  const showSuccess = (message: string): void => {
    const success = el(doc, 'div', { className: 'stoq-form-success', attrs: { role: 'status' } })
    success.innerHTML =
      '<svg class="stoq-form-success-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">'
      + '<path fill="currentColor" d="M13.28 9.03a.75.75 0 0 0-1.06-1.06l-2.97 2.97-1.22-1.22a.75.75 0 0 0-1.06 1.06l1.75 1.75a.75.75 0 0 0 1.06 0l3.5-3.5Z"/>'
      + '<path fill="currentColor" fill-rule="evenodd" d="M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Zm-1.5 0a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0Z"/>'
      + '</svg>'
    success.appendChild(el(doc, 'div', { html: message }))
    form.replaceWith(success)
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault()
    clearError()

    const validated = validateForm(
      { email: emailInput?.value, phone: phoneInput?.value },
      channels,
    )
    if (validated.errors.length > 0) {
      if (validated.errors.includes('email')) emailInput?.classList.add('stoq-form-input--error')
      if (validated.errors.includes('phone')) phoneInput?.classList.add('stoq-form-input--error')
      setError(validated.errors.map((kind) => errorTextFor(kind, texts)).join(' '), false)
      return
    }

    button.disabled = true
    void ctx
      .createSignup(buildSignupInput(options, validated))
      .then((result) => {
        const outcome = mapSubmitResult(result)
        if (outcome.state === 'success') {
          showSuccess(successMessage(texts, ctx.settings, usedEmailChannel(validated, result.intents)))
        } else if (outcome.state === 'already-registered') {
          setError(texts.duplicateError, true)
        } else {
          setError(errorTextFor(outcome.kind, texts), false)
        }
      })
      .catch((error: unknown) => {
        warn('signup failed', error)
        setError(texts.genericError, false)
      })
      .then(() => {
        button.disabled = false
      })
  })

  return root
}

/**
 * Opens the notify-me modal. Replaces any modal that is already open. Fires
 * `stoq:restock-modal:opened` now and `stoq:restock-modal:closed` when
 * dismissed (close button, backdrop click, ESC or `handle.close()`).
 * `stoq:restock-modal:submitted` fires on successful signup (via
 * createSignup). Browser-only — throws during SSR.
 */
export function openModal(ctx: ModalContext, options: ModalOptions): ModalHandle {
  const variantId = variantIdOf(options.variantId)
  const doc = requireDocument('openModal')

  activeModal?.close()
  injectStyles(doc)

  const styles = resolveStyles(ctx.settings, options.styles)
  const overlay = el(doc, 'div', { className: 'stoq-modal-overlay' })
  overlay.style.zIndex = styles.zIndex

  let closed = false
  const previousActive = doc.activeElement
  const handle: ModalHandle = {
    element: overlay,
    close: () => {
      if (closed) return
      closed = true
      if (activeModal === handle) activeModal = null
      doc.removeEventListener('keydown', onKeydown, true)
      overlay.remove()
      emit(EVENT_MODAL_CLOSED, eventDetail('closed', options, variantId))
      if (previousActive instanceof HTMLElement) previousActive.focus()
    },
  }

  const dialog = buildForm(ctx, options, doc, false, handle.close)

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault()
      handle.close()
      return
    }
    trapFocus(dialog, event)
  }

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) handle.close()
  })
  doc.addEventListener('keydown', onKeydown, true)

  overlay.appendChild(dialog)
  doc.body.appendChild(overlay)
  activeModal = handle

  emit(EVENT_MODAL_OPENED, eventDetail('opened', options, variantId))
  focusFirstInput(dialog)

  return handle
}

/**
 * Renders the same signup form into (or next to) a merchant-supplied
 * container. Returns null (with a console warning) when the container cannot
 * be found. Re-opening against the same container replaces the previous
 * form. Does not fire opened/closed events (matches the theme embed); the
 * `submitted` event still fires on success. Browser-only — throws during SSR.
 */
export function openInlineForm(ctx: ModalContext, options: InlineFormOptions): ModalHandle | null {
  variantIdOf(options.variantId) // Validate early, same as openModal.
  const doc = requireDocument('openInlineForm')

  const container = typeof options.container === 'string'
    ? doc.querySelector(options.container)
    : options.container
  if (!container) {
    warn(`openInlineForm container not found: ${String(options.container)}`)
    return null
  }

  // Replace an existing form mounted against the same container.
  for (const [root, mountedContainer] of inlineForms) {
    if (mountedContainer === container) {
      root.remove()
      inlineForms.delete(root)
    }
  }

  injectStyles(doc)

  const root = buildForm(ctx, options, doc, true, null)
  let closed = false
  const handle: ModalHandle = {
    element: root,
    close: () => {
      if (closed) return
      closed = true
      root.remove()
      inlineForms.delete(root)
    },
  }

  container.insertAdjacentElement(options.insert ?? 'beforeend', root)
  inlineForms.set(root, container)

  return handle
}

/**
 * Removes inline forms rendered by {@link openInlineForm}. With no argument
 * all inline forms are removed (parity with the theme embed's
 * `removeInlineForm()`); pass the container element or a CSS selector to
 * remove a specific one. No-op during SSR or when nothing is mounted.
 */
export function removeInlineForm(container?: Element | string): void {
  if (inlineForms.size === 0) return

  let target: Element | null = null
  if (container !== undefined) {
    if (typeof container === 'string') {
      if (typeof document === 'undefined') return
      target = document.querySelector(container)
      if (!target) return
    } else {
      target = container
    }
  }

  for (const [root, mountedContainer] of inlineForms) {
    const matches = target === null
      || mountedContainer === target
      || target.contains(root)
      || target === root
    if (matches) {
      root.remove()
      inlineForms.delete(root)
    }
  }
}
